import { getCollectorOnCase } from '@debbie/common/dist/cases/index.js';
import { SecondaryEmailType, getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { FeatureFlag } from '@debbie/common/dist/feature-flags/index.js';
import { Entity } from '@debbie/common/dist/index.js';
import { IntentType } from '@debbie/common/dist/intents/index.js';
import { Direction, InteractionType, OutgoingState, } from '@debbie/common/dist/interactions/index.js';
import { TemplateType } from '@debbie/common/dist/templates/index.js';
import { toEditorMentions } from '@debbie/common/dist/templates/suggestions.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { cancelDebounce, debounce } from '@debbie/common/dist/util/index.js';
import { useAIUsage } from '@debbie/cortex/dist/ai/index.js';
import { genSuggestion } from '@debbie/cortex/dist/api/ai.js';
import { useCase } from '@debbie/cortex/dist/cases/index.js';
import { useCollector } from '@debbie/cortex/dist/collectors/index.js';
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import { useCustomer } from '@debbie/cortex/dist/customers/index.js';
import { useDocumentsByReference } from '@debbie/cortex/dist/documents/index.js';
import { useEmailServer } from '@debbie/cortex/dist/email-servers/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { useNewInteraction } from '@debbie/cortex/dist/newInteractions/index.js';
import { emailInitialState } from '@debbie/cortex/dist/newInteractions/types.js';
import { session } from '@debbie/cortex/dist/sessions.js';
import { useMentionSuggestions } from '@debbie/cortex/dist/templates/index.js';
import { useTenant } from '@debbie/cortex/dist/tenant/index.js';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { keyframes, styled } from 'styled-components';
import { useVariation } from '../../feature-flags/index.js';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import { encodeText, getSubjectContent, parseText, } from '../../utils/templates.js';
import { File, FileDropzone, Input, Intent, Modal } from '..//index.js';
import AI from '../AI.js';
import ContactSelect, { hasEmail } from '../ContactSelect.js';
import NewEditor from '../editor/Editor.js';
import { isEditorEmpty, useEditorState } from '../editor/hooks.js';
import { htmlToPlainText } from '../editor/utils.js';
import { AddFromTemplate } from './/index.js';
import InteractionFooter, { AddDebtorDocumentsAction, AddIntentAction, AddTemplateAction, AttachAction, PreviewAction, SendButton, } from './InteractionFooter.js';
import PreviewInteraction from './PreviewInteraction.js';
import TemplateSearch from './TemplateSearch.js';
import VisibilityToggle from './VisibilityToggle.js';
const EmailInteraction = (props) => {
    const hasAiSuggestions = useVariation(FeatureFlag.AISuggestions);
    const isCase = props.referenceType === Entity.Case;
    const singleCaseLoader = useCase(props.referenceType === Entity.Case ? props.referenceId : undefined);
    const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null;
    const customerLoader = useCustomer(singleCase?.customerId);
    const customer = h.hasData(customerLoader) ? customerLoader.data : null;
    const creditorId = singleCase?.creditorId ||
        (props.referenceType === Entity.Creditor && props.referenceId);
    const tenant = useTenant();
    const creditorLoadable = useCreditor(creditorId || undefined);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const documentsLoadable = useDocumentsByReference(props.referenceType === Entity.Case ? props.referenceId : undefined);
    const documents = h.hasData(documentsLoadable) ? documentsLoadable.data : null;
    const [emailPreviewModalOpen, setEmailPreviewModalOpen] = useState(false);
    const [emailTemplateAdderOpen, setEmailTemplateAdderOpen] = useState(false);
    const dropzoneRef = useRef(null);
    const [emailToInput, setEmailToInput] = useState(props.emailTo);
    const [emailCCInput, setEmailCCInput] = useState('');
    const [emailBCCInput, setEmailBCCInput] = useState('');
    const [contactId, setContactId] = useState(null);
    const [ccAndBccOpen, setCcAndBccOpen] = useState(false);
    const [visibleToClients, setVisibleToClients] = useState(true);
    const lang = useSelector((s) => s.lang);
    const [interaction, setInteraction] = useNewInteraction(props.referenceId);
    const email = interaction.email;
    const uploadedFiles = interaction.uploadedFiles;
    const [clean, setClean] = useState(true);
    const subjectState = useEditorState(props.defaultSubject ? props.defaultSubject : (email.subject ?? ''));
    const bodyState = useEditorState(props.defaultContent ? props.defaultContent : (email.content ?? ''));
    const caseGroupId = singleCase?.caseGroup?.id || null;
    const mentionSuggestions = useMentionSuggestions(TemplateType.EMAIL, caseGroupId);
    const letterMentionSuggestions = useMentionSuggestions(TemplateType.LETTER, caseGroupId);
    const collectorId = props.referenceType === Entity.Creditor
        ? tenant?.collectorIdentityId
        : singleCase &&
            creditor &&
            getCollectorOnCase(singleCase.type, singleCase.collectorId, creditor.identityCollectorId, creditor.defaultCollectorId);
    const collectorLoadable = useCollector(collectorId || undefined);
    const collector = h.hasData(collectorLoadable) ? collectorLoadable.data : null;
    const emailServerLoadable = useEmailServer(collector?.emailServerId);
    const emailServer = h.hasData(emailServerLoadable)
        ? emailServerLoadable.data
        : null;
    useEffect(() => {
        if (customer && props.isDebtor && singleCase) {
            const contact = getContactPerson(customer, singleCase);
            if (contact) {
                const secondaryEmails = (contact.details.secondaryEmails || []).filter((email) => email.enabled);
                if (secondaryEmails.length > 0) {
                    setCcAndBccOpen(true);
                    const toEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.To);
                    if (toEmails.length) {
                        setEmailToInput(`${emailToInput};${toEmails.map(({ email }) => email).join(';')}`);
                    }
                    const ccEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.CC);
                    if (ccEmails.length) {
                        setEmailCCInput(ccEmails.map(({ email }) => email).join(';'));
                    }
                    const bccEmails = secondaryEmails.filter((email) => email.type === SecondaryEmailType.BCC);
                    if (bccEmails.length) {
                        setEmailBCCInput(bccEmails.map(({ email }) => email).join(';'));
                    }
                }
            }
        }
    }, [customer, singleCase]);
    useEffect(() => {
        if (props.defaultSubject) {
            return;
        }
        if (Object.keys(email).length > 0 && clean) {
            if (email.templateId || !isEditorEmpty(email.subject ?? '')) {
                setClean(false);
                return;
            }
            let subject = '';
            if (props.referenceType === Entity.Case) {
                const genericSubject = encodeText('{{genericEmailSubject}}', mentionSuggestions);
                const courtSubject = encodeText(lang.interactions.courtEmailSubject, mentionSuggestions);
                subject = props.isCourt ? courtSubject : genericSubject;
            }
            else if (props.referenceType === Entity.Creditor) {
                if (collector) {
                    subject = `${lang.interactions.emailSubjectMessageFrom} ${collector.name}`;
                }
            }
            if (subject) {
                subjectState.overwriteEditorContent(subject);
                setInteraction('email', { ...email, subject: subject });
            }
            setClean(false);
        }
    }, [props.isCourt, email, collector, clean]);
    const hasSubject = () => !isEditorEmpty(subjectState.html);
    const hasBody = () => !isEditorEmpty(bodyState.html);
    const toggleAddTemplateModal = () => {
        setEmailTemplateAdderOpen(!emailTemplateAdderOpen);
    };
    const toggleEmailPreviewModal = () => {
        setEmailPreviewModalOpen(!emailPreviewModalOpen);
        if (singleCase != null && !emailPreviewModalOpen) {
            session.createEvent('previewed_interaction', {
                caseId: singleCase.caseId,
                interactionType: InteractionType.Email,
            });
        }
    };
    const sendEmailMutation = useMutation(async () => {
        if (!emailServer || !email || !emailToInput) {
            return;
        }
        cancelDebounce('edited-interaction-session-event');
        if (singleCase != null && bodyState.html !== '<p></p>') {
            session.createEvent('edited_interaction', {
                caseId: singleCase.caseId,
                interactionType: InteractionType.Email,
            });
        }
        await c.newInteractions.sendEmail(props.referenceId, props.referenceType, email.templateId, {
            server: emailServer.id,
            subject: getSubjectContent(htmlToPlainText(parseText(subjectState.html, mentionSuggestions))),
            from: `${emailServer.fromName} <${emailServer.fromEmail}>`,
            replyTo: collector?.replyTo,
            body: htmlToPlainText(parseText(bodyState.html, mentionSuggestions)),
            html: parseText(bodyState.html, mentionSuggestions),
            to: emailToInput,
            cc: emailCCInput,
            bcc: emailBCCInput,
        }, (uploadedFiles || []).map((f) => f.fileId), visibleToClients ? 'client' : 'caseworker', email.intent, props.replyingToInteractionId);
        setEmailToInput(props.emailTo);
        setEmailCCInput('');
        setEmailBCCInput('');
        setContactId(null);
        setClean(true);
        subjectState.overwriteEditorContent(emailInitialState.subject ?? '<p></p>');
        bodyState.overwriteEditorContent(emailInitialState.content);
        if (singleCase != null) {
            session.createEvent('submitted_interaction', {
                caseId: singleCase.caseId,
                interactionType: InteractionType.Email,
            });
        }
        if (props.onSent) {
            props.onSent();
        }
    });
    const sendEmail = () => {
        sendEmailMutation.mutate();
    };
    const documentsVisibleForDebtorToAdd = documents
        ? documents.filter((document) => document.minAccessRole === 'debtor' &&
            !uploadedFiles.find((uploadedFile) => uploadedFile.fileId === document.file?.fileId))
        : null;
    return (React.createElement(React.Fragment, null,
        React.createElement(FileDropzone, { onChange: (file) => setInteraction('uploadedFiles', uploadedFiles.concat([file])), dropzoneRef: dropzoneRef },
            React.createElement("p", { style: { fontSize: '0.8em' } }, emailServer
                ? `${emailServer.fromName} <${emailServer.fromEmail}>`
                : ''),
            email && singleCase && customer && (React.createElement(TemplateSearch, { interaction: email, templateType: TemplateType.EMAIL, case: singleCase, customer: customer, onChange: (state, subject) => {
                    bodyState.overwriteEditorContent(state);
                    if (subject != null) {
                        if (props.defaultSubject == null) {
                            subjectState.overwriteEditorContent(subject);
                        }
                    }
                    if (singleCase != null) {
                        session.createEvent('changed_interaction_template', {
                            caseId: singleCase.caseId,
                            interactionType: InteractionType.Email,
                        });
                    }
                } })),
            props.contactRecipient === true && (React.createElement(ContactSelect, { value: contactId, filter: hasEmail, onChange: (id, contact) => {
                    setContactId(id);
                    setEmailToInput(contact?.email ?? '');
                } })),
            React.createElement(Input, { disabled: typeof props.emailTo !== 'undefined' && !props.emailToEditable, placeholder: lang.interactions.to, value: emailToInput || '', onChange: (e) => {
                    setEmailToInput(e.target.value);
                    setContactId(null);
                } }),
            ccAndBccOpen ? (React.createElement(React.Fragment, null,
                React.createElement(Input, { placeholder: "CC", value: emailCCInput, onChange: (e) => {
                        setEmailCCInput(e.target.value);
                    } }),
                React.createElement(Input, { placeholder: "BCC", value: emailBCCInput, onChange: (e) => {
                        setEmailBCCInput(e.target.value);
                    } }))) : (React.createElement("div", { style: {
                    position: 'relative',
                    marginBottom: '5px',
                } },
                React.createElement("div", { style: {
                        position: 'absolute',
                        fontWeight: 900,
                        fontSize: '0.7em',
                        cursor: 'pointer',
                        color: THEME.COLOR_ACCENT,
                        top: '-19px',
                        right: '0px',
                    }, onClick: () => setCcAndBccOpen(!ccAndBccOpen) }, "CC, BCC"))),
            React.createElement(NewEditor, { hideControls: true, compact: true, singleLine: true, placeholder: lang.interactions.subject, mentions: toEditorMentions(mentionSuggestions), initialContent: subjectState.editorContent, onChange: (html) => {
                    subjectState.onChange(html);
                    setInteraction('email', { ...email, subject: html });
                } }),
            React.createElement("br", null),
            React.createElement(NewEditor, { mentions: toEditorMentions(mentionSuggestions), mentionSuggestions: mentionSuggestions, onChange: (html) => {
                    const previousHtml = bodyState.html;
                    bodyState.onChange(html);
                    setInteraction('email', { ...email, content: html });
                    if (singleCase != null && previousHtml !== html) {
                        if (html === '<p></p>') {
                            session.createEvent('deleted_interaction_content', {
                                caseId: singleCase.caseId,
                                interactionType: InteractionType.Email,
                            });
                            debounce('edited-interaction-session-event', () => { }, 1000);
                        }
                        else {
                            debounce('edited-interaction-session-event', () => {
                                session.createEvent('edited_interaction', {
                                    caseId: singleCase.caseId,
                                    interactionType: InteractionType.Email,
                                });
                            }, 1000);
                        }
                    }
                }, initialContent: bodyState.editorContent }),
            React.createElement("div", { style: { display: 'flex', justifyContent: 'flex-end' } }, hasAiSuggestions && singleCase != null && (React.createElement(AIGen, { caseId: singleCase.caseId, templateType: TemplateType.EMAIL, recipient: emailToInput ?? props.emailTo ?? 'unknown', respondingTo: props.replyingToInteractionId ?? null, onTextGenerated: (text) => {
                    const html = encodeText(text, mentionSuggestions);
                    bodyState.overwriteEditorContent(html);
                } }))),
            React.createElement("br", null),
            !!(uploadedFiles || [])?.length && (React.createElement("div", { style: { marginTop: '12px' } }, (uploadedFiles || []).map((file, i) => (React.createElement(File, { key: file.fileId, file: file, onRemove: () => setInteraction('uploadedFiles', [
                    ...uploadedFiles.slice(0, i),
                    ...uploadedFiles.slice(i + 1),
                ]), removable: true }))))),
            props.isDebtor &&
                email &&
                isCase &&
                email.intent.type !== IntentType.Unknown && (React.createElement("div", { style: { margin: '1em 0 0 0' } },
                React.createElement(Intent, { intent: email.intent, editable: true, onEdit: (intent) => {
                        setInteraction('email', { ...email, intent: intent });
                    } }))),
            email && (React.createElement(InteractionFooter, { error: sendEmailMutation.error, primary: React.createElement(React.Fragment, null,
                    React.createElement(SendButton, { loading: email.loading || sendEmailMutation.isLoading, disabled: !(emailToInput && hasSubject() && hasBody() && emailServer), onClick: sendEmail, id: "ui-test-9c459973" }, lang.interactions.send),
                    isCase && (React.createElement(PreviewAction, { onClick: toggleEmailPreviewModal, disabled: !hasBody() }))), actions: React.createElement(React.Fragment, null,
                    React.createElement(AttachAction, { onClick: () => dropzoneRef.current?.open() }),
                    isCase && (React.createElement(React.Fragment, null,
                        React.createElement(AddTemplateAction, { onClick: toggleAddTemplateModal }),
                        React.createElement(AddDebtorDocumentsAction, { documents: documentsVisibleForDebtorToAdd, onAdd: (files) => setInteraction('uploadedFiles', [
                                ...uploadedFiles,
                                ...files,
                            ]) }),
                        props.isDebtor &&
                            email.intent.type === IntentType.Unknown && (React.createElement(AddIntentAction, { onAdd: (intent) => setInteraction('email', { ...email, intent }) })),
                        React.createElement(VisibilityToggle, { visible: visibleToClients, onChange: setVisibleToClients })))) }))),
        emailTemplateAdderOpen && isCase && singleCase && customer && (React.createElement(AddFromTemplate, { toggleAddTemplateModal: toggleAddTemplateModal, case: singleCase, customer: customer, mentionSuggestions: letterMentionSuggestions })),
        emailPreviewModalOpen && isCase && singleCase && email && (React.createElement(Modal, { onToggle: toggleEmailPreviewModal, header: lang.interactions.preview },
            emailServer && emailToInput && (React.createElement(PreviewInteraction, { interaction: {
                    templateId: email.templateId || null,
                    direction: Direction.OUTGOING,
                    state: OutgoingState.SENT,
                    type: InteractionType.Email,
                    attachments: (uploadedFiles || []).map((f) => f.fileId),
                    referenceId: props.referenceId,
                    referenceType: props.referenceType,
                    details: {
                        server: emailServer.id,
                        subject: getSubjectContent(htmlToPlainText(parseText(subjectState.html, mentionSuggestions))),
                        from: `${emailServer.fromName} <${emailServer.fromEmail}>`,
                        replyTo: collector?.replyTo,
                        body: htmlToPlainText(parseText(bodyState.html, mentionSuggestions)),
                        html: parseText(bodyState.html, mentionSuggestions),
                        to: emailToInput,
                        cc: emailCCInput,
                        bcc: emailBCCInput,
                    },
                    minVisibilityRole: visibleToClients ? 'client' : 'caseworker',
                    intent: email.intent,
                    replyingToInteractionId: props.replyingToInteractionId ?? undefined,
                } })),
            React.createElement("br", null),
            React.createElement(SendButton, { loading: email.loading || sendEmailMutation.isLoading, disabled: !(emailToInput && hasSubject() && hasBody() && emailServer), onClick: () => {
                    sendEmail();
                    setEmailPreviewModalOpen(false);
                }, id: "ui-test-9c459973" }, lang.interactions.send)))));
};
export default EmailInteraction;
export const AIGen = (props) => {
    const lang = useSelector((s) => s.lang);
    const aiUsageLoader = useAIUsage();
    const isAtLimit = h.hasData(aiUsageLoader) && aiUsageLoader.data.remaining <= 0;
    const [prompt, setPrompt] = useState('');
    const suggestion = useMutation(genSuggestion, {
        onSuccess: (res) => {
            props.onTextGenerated(res.content);
            session.createEvent('generated_interaction_suggestion', {
                caseId: props.caseId,
                replyingTo: props.respondingTo ?? undefined,
                templateType: props.templateType,
                interactionType: InteractionType.Email,
                prompt: prompt != '' ? prompt : undefined,
            });
        },
        onError: (err) => {
            errorNotification(lang, err, 'create', lang.errorEntities.aiSuggestion);
        },
    });
    const onClick = () => {
        if (isAtLimit) {
            return;
        }
        suggestion.mutate({
            caseId: props.caseId,
            recipient: props.recipient,
            respondingTo: props.respondingTo,
            templateType: props.templateType,
            userPrompt: prompt,
        });
        setPrompt('');
    };
    return (React.createElement(LiveAI, { visible: suggestion.isLoading },
        React.createElement(LiveAIInput, { onKeyUp: (e) => {
                if (e.key === 'Enter') {
                    onClick();
                }
            }, placeholder: 'Prompt', disabled: suggestion.isLoading || isAtLimit, value: prompt, onChange: (e) => {
                setPrompt(e.currentTarget.value);
            } }),
        React.createElement(AI, { loading: suggestion.isLoading, disabled: isAtLimit, "data-tooltip-id": "tooltip-default", "data-tooltip-content": isAtLimit
                ? lang.autoReply.usageLimitReached
                : lang.autoReply.generateSuggestion, height: "28px", onClick: onClick })));
};
const LiveAIInput = styled.input `
  transition: opacity 300ms ease-in-out;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  padding: 2px;

  border: none;

  &:focus,
  &:hover {
    outline: none;
    opacity: 1;
    pointer-events: initial;
  }
`;
const LiveAIWidth = keyframes `
  0% {
    width: 48px;
  }
  100% {
    width: 48px;
  }
`;
const LiveAI = styled.div `
  transition:
    background 300ms ease-in-out,
    opacity 300ms ease-in-out,
    box-shadow 300ms ease-in-out;
  width: calc(100% - 8px);
  margin: 0px 4px;

  display: flex;
  align-items: center;
  position: absolute;
  transform: translateY(calc(-100% - 5px));
  /*pointer-events: none;*/

  animation: ${LiveAIWidth} 1s infinite;
  animation-delay: 300ms;

  border-radius: 100000px;
  padding: 4px;
  padding-left: 12px;
  opacity: ${(props) => (props.visible ? '1' : '0.6')};

  &:has(.ai:hover) ${LiveAIInput}, &:has(.ai:hover),
  &:has(${LiveAIInput}:hover),
  &:has(${LiveAIInput}:focus) {
    opacity: 1;
    pointer-events: auto;
    background: white;
  }

  &:has(.ai:hover),
  &:has(${LiveAIInput}:hover),
  &:has(${LiveAIInput}:focus) {
    box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.3);
    width: calc(100% - 8px);
    animation: none;
  }
`;
const AIWrapper = styled.div `
  transition: opacity 300ms ease-in-out;

  position: absolute;
  right: 4px;
  transform: translateY(calc(-100% - 5px));
  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;
//# sourceMappingURL=EmailInteraction.js.map